// Over / Under Rates — types + shaping (Phase 3).
//
// For one team, the % of recent games that went over each goal line (0.5–4.5).
// Each line is a split bar: a green "over" fill + a red "under" fill that sum to
// 100%. Pure + framework-free; the D3 width animation lives in the component.

/** Over-rates for one window (percent of games over each line). */
export interface OverUnderWindow {
  o05: number;
  o15: number;
  o25: number;
  o35: number;
  o45: number;
}

/** Over/Under data keyed by full team name, then window. */
export type OverUnderData = Record<
  string,
  Record<"5" | "10" | "Season", OverUnderWindow>
>;

/** One goal line: its data key, display label, and the over-percentage. */
export interface OverUnderLine {
  key: keyof OverUnderWindow;
  label: string;
  pct: number;
}

/** Shaped widget: the team + its five lines. */
export interface OverUnderView {
  team: string;
  lines: OverUnderLine[];
}

/** The five goal lines in display order, with their labels (legacy format "O x.x"). */
const LINES: { key: keyof OverUnderWindow; label: string }[] = [
  { key: "o05", label: "O 0.5" },
  { key: "o15", label: "O 1.5" },
  { key: "o25", label: "O 2.5" },
  { key: "o35", label: "O 3.5" },
  { key: "o45", label: "O 4.5" },
];

/** Normalise the timeline control to a data key (anything but 10/Season → 5). */
function toWindow(timeline: string): "5" | "10" | "Season" {
  return timeline === "10" ? "10" : timeline === "Season" ? "Season" : "5";
}

/**
 * Shape one team's over/under lines for a window. Returns null when the
 * team/window is missing (empty state).
 */
export function shapeOverUnder(
  data: OverUnderData,
  team: string,
  timeline: string
): OverUnderView | null {
  const td = data[team]?.[toWindow(timeline)];
  if (!td) return null;
  return {
    team,
    lines: LINES.map((l) => ({ key: l.key, label: l.label, pct: td[l.key] })),
  };
}
